import { Box, Chip, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { useContext } from 'react'

import { Entry, EntryStatus, EntryType, Maybe } from '../../../generated'
import { FILTER_DATE_FORMAT, NOT_APPLICABLE, SEND_NOTES_COLUMN } from '../../constants'
import { AuthContext } from '../../context'
import { TimeEntryListingProps } from '../../interfaceTypes/props'
import { CustomPagination } from '../common/CustomPagination'
import { CustomTableCell } from '../common/CustomTableCell'
import { StickyTable } from '../common/StickyTable'
import { TableSkeleton } from '../skeletons/TableSkeleton'
import { EntryMenu } from './EntryMenu'
import { EntrySourceIcon } from './EntrySourceIcon'
import { EntryTypeScope } from './EntryTypeScope'
import dayjs from 'dayjs'

export const ALL_ENTRIES_HEADERS: readonly string[] = [
  'Matter ID',
  'Matter',
  'Type',
  'User',
  'Date',
  'Time',
  'Rate',
  'Fee Type',
  'Amount',
  'Category',
  'Description',
  'Billable',
  'Show On Invoice',
  SEND_NOTES_COLUMN,
  'Status',
  'Actions'
]

const ENTRY_TYPE_LABELS: Record<string, string> = {
  [EntryType.Time]: 'Time',
  [EntryType.Legal]: 'Legal',
  [EntryType.Expense]: 'Expense',
  [EntryType.Cost]: 'Investigator',
  [EntryType.NonClient]: 'Non-Client'
}

const isTimeOrCost = (entryType: string | null | undefined) =>
  entryType === EntryType.Time || entryType === EntryType.Cost
const isLegal = (entryType: string | null | undefined) => entryType === EntryType.Legal
const isExpense = (entryType: string | null | undefined) => entryType === EntryType.Expense

/** One entry row of the All Entries table. */
export const AllEntriesRow = ({
  entry,
  showColumns,
  entryDispatch,
  refetch,
  lockCase,
  scopeMenuToEntryType = false
}: {
  entry: Maybe<Entry> | undefined
  showColumns: string[]
  entryDispatch: TimeEntryListingProps['entryDispatch']
  refetch?: () => void
  lockCase?: boolean
  /** Give the row's menu (edit / delete) the entry-type context of the entry itself, so an expense
   *  row opens the expense form rather than whichever form the page-level flags describe. */
  scopeMenuToEntryType?: boolean
}) => {
  const { isAdmin, isParalegal } = useContext(AuthContext)
  const getAmount = (duration: string | null | undefined, rate: number, amount: number | null | undefined) => {
    if (amount != null) return amount.toFixed(2)
    if (duration && rate != null) {
      const [h = '0', m = '0', s = '0'] = String(duration).split(':')
      const hours = Number(h) + Number(m) / 60 + Number(s) / 3600
      return (hours * Number(rate)).toFixed(2)
    }
    return '0'
  }

  const {
    id,
    billable,
    case: cases,
    user,
    creator,
    amount,
    duration,
    status,
    category,
    rate,
    showOnBill,
    date,
    createdAt,
    description,
    sendNotesToMatterNotes,
    feeType: feeTypes,
    entryType,
    startTime
  } = entry || {}

  const { feeType } = feeTypes || {}
  const { id: caseId, title, caseNumber, isConsultMatter, consultMatterNumber } = cases ?? {}
  const { firstName, lastName } = user ?? {}
  const creatorName =
    creator?.firstName != null || creator?.lastName != null
      ? [creator?.firstName, creator?.lastName].filter(Boolean).join(' ')
      : null
  const { name: categoryName } = category ?? {}

  return (
    <TableRow key={id} hover>
      {showColumns.includes('Matter ID') && (
        <CustomTableCell
          value={`#${isConsultMatter ? consultMatterNumber : caseNumber}`}
          to={`/matters/matter-detail/${caseId}`}
          maxWidth="376px"
        />
      )}
      {showColumns.includes('Matter') && (
        <CustomTableCell value={`${title ?? ''}`} maxWidth="180px" noWrap={false} />
      )}

      {showColumns.includes('Type') && (
        <TableCell align="left" sx={{ maxWidth: 120 }}>
          <Chip
            label={ENTRY_TYPE_LABELS[entryType ?? ''] ?? entryType ?? NOT_APPLICABLE}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.75rem' }}
          />
        </TableCell>
      )}

      {showColumns.includes('User') && (isAdmin || isParalegal) && (
        <TableCell align="left" sx={{ maxWidth: 180 }}>
          <Typography
            component="span"
            variant="body2"
            sx={{ fontSize: '0.70rem', display: 'block', marginBottom: '2px' }}
          >
            Work Performed:
          </Typography>
          <Typography
            component="span"
            variant="body2"
            sx={{ fontSize: '0.90rem', display: 'block', marginBottom: '8px' }}
          >
            {firstName != null || lastName != null
              ? [firstName, lastName].filter(Boolean).join(' ')
              : NOT_APPLICABLE}
          </Typography>
          <Typography
            component="span"
            variant="body2"
            sx={{ fontSize: '0.75rem', display: 'block', marginBottom: '2px' }}
          >
            Work Logged:
          </Typography>
          <Typography component="span" variant="body2" sx={{ fontSize: '0.90rem', display: 'block' }}>
            {creatorName ?? NOT_APPLICABLE}
          </Typography>
        </TableCell>
      )}

      {showColumns.includes('Date') && (
        <TableCell align="left" sx={{ maxWidth: 160 }}>
          <Typography
            component="span"
            variant="body2"
            sx={{ fontSize: '0.70rem', display: 'block', marginBottom: '2px' }}
          >
            Activity Date:
          </Typography>
          <Typography
            component="span"
            variant="body2"
            sx={{ fontSize: '0.90rem', display: 'block', marginBottom: '8px' }}
          >
            {date ? dayjs(date).format(FILTER_DATE_FORMAT) : NOT_APPLICABLE}
          </Typography>
          <Typography
            component="span"
            variant="body2"
            sx={{ fontSize: '0.70rem', display: 'block', marginBottom: '2px' }}
          >
            Logged Date:
          </Typography>
          <Typography component="span" variant="body2" sx={{ fontSize: '0.90rem', display: 'block' }}>
            {createdAt && !dayjs(createdAt).isBefore(dayjs('2026-02-09'), 'day')
              ? dayjs(createdAt).format(FILTER_DATE_FORMAT)
              : NOT_APPLICABLE}
          </Typography>
        </TableCell>
      )}

      {showColumns.includes('Time') && (
        <TableCell align="left">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography variant="body1" noWrap>
              {isTimeOrCost(entryType) ? duration?.toString() || '0' : NOT_APPLICABLE}
            </Typography>
            {isTimeOrCost(entryType) && <EntrySourceIcon startTime={startTime} />}
          </Box>
        </TableCell>
      )}
      {showColumns.includes('Rate') && (
        <CustomTableCell
          value={isTimeOrCost(entryType) && rate != null ? `$${rate}` : NOT_APPLICABLE}
        />
      )}
      {showColumns.includes('Fee Type') && (
        <CustomTableCell value={isLegal(entryType) ? (feeType || NOT_APPLICABLE) : NOT_APPLICABLE} />
      )}
      {showColumns.includes('Amount') && (
        <CustomTableCell value={`$${getAmount(duration, rate ?? 0, amount)}`} />
      )}
      {showColumns.includes('Category') && (
        <CustomTableCell value={isExpense(entryType) ? (categoryName || '') : NOT_APPLICABLE} />
      )}

      {showColumns.includes('Description') && (
        <CustomTableCell noWrap={false} value={description || NOT_APPLICABLE} maxWidth="400px" />
      )}

      {showColumns.includes('Billable') && <CustomTableCell value={!!billable} />}
      {showColumns.includes('Show On Invoice') && (
        <CustomTableCell value={!!showOnBill} maxWidth="500px" />
      )}
      {showColumns.includes(SEND_NOTES_COLUMN) && <CustomTableCell value={!!sendNotesToMatterNotes} />}

      {showColumns.includes('Status') && (
        <CustomTableCell value={status || EntryStatus.Draft} />
      )}

      {showColumns.includes('Actions') && (
        <TableCell>
          {status === EntryStatus.Unbilled && (
            scopeMenuToEntryType ? (
              <EntryTypeScope entryType={entryType}>
                <EntryMenu
                  currentTimeEntry={entry as Entry}
                  entryDispatch={entryDispatch}
                  refetch={refetch}
                  lockCase={lockCase}
                />
              </EntryTypeScope>
            ) : (
              <EntryMenu
                currentTimeEntry={entry as Entry}
                entryDispatch={entryDispatch}
                refetch={refetch}
                lockCase={lockCase}
              />
            )
          )}
        </TableCell>
      )}
    </TableRow>
  )
}

const AllEntriesListing = ({ state, entryDispatch, loading, refetch, lockCase }: TimeEntryListingProps) => {
  const { allEntries } = state || {}
  const { isAdmin, isParalegal } = useContext(AuthContext)
  const showColumns: string[] = ALL_ENTRIES_HEADERS.filter(
    (col: string) => (isAdmin || isParalegal) || col !== 'User'
  )

  return (
    <StickyTable pagination={<CustomPagination loading={loading || false} />} height="405px">
      <TableHead>
        <TableRow hover>
          {showColumns.map((columnName: string, index: number) => (
            <TableCell
              key={index}
              component="th"
              align={columnName === 'Billable' || columnName === 'Show On Invoice' ? 'center' : 'left'}
            >
              {columnName}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>

      {loading ? (
        <TableSkeleton noOfRows={7} noOfCols={showColumns.length} />
      ) : (
        <TableBody>
          {allEntries?.map(entry => (
            <AllEntriesRow
              key={entry?.id}
              entry={entry}
              showColumns={showColumns}
              entryDispatch={entryDispatch}
              refetch={refetch}
              lockCase={lockCase}
            />
          ))}
        </TableBody>
      )}
    </StickyTable>
  )
}

export default AllEntriesListing
